import { COLLECTION_PLACES_PER_PAGE } from "@/constants/collection"
import {
  Collection,
  CollectionRef,
  CollectionResponse,
  CollectionSummary,
  FavoriteCollection,
} from "@/types/collection"
import { PlaceRef } from "@/types/entity"
import { FavoritePlace } from "@/types/place"
import { SavedCollectionsStats } from "@/types/saved"
import Honeybadger from "@honeybadger-io/js"
import { createSelector } from "@reduxjs/toolkit"

import { now } from "@/lib/date-utils"
import { errorMessage } from "@/lib/exception-utils"
import { DEFAULT_PLACES_PER_PAGE, PagingMeta } from "@/lib/paging"
import { toastManager } from "@/lib/toast"
import { getConstantEmptySet } from "@/lib/utils/collections"
import { RootState } from "../redux-store"
import { baseApi } from "./base-api"

export interface GetCollectionArgs {
  ref: CollectionRef
  page?: number
  perPage?: number
  viewToken?: string
}

export interface GetCollectionsArgs {
  isPublic?: boolean
  page?: number
  perPage?: number
  placeRef?: PlaceRef
  q?: string
  // Also list the public collections the user saved without owning or
  // collaborating on them. Only the Saved page's Collections tab sets this;
  // the user cannot add places to those collections.
  includeFavorites?: boolean
}

export interface GetCollectionsResponse {
  data: CollectionSummary[]
  meta: PagingMeta
}

export interface GetPublicUserCollectionsArgs {
  handle: string
  page?: number
  perPage?: number
}

export interface GetPublicUserCollectionsResponse {
  data: CollectionSummary[]
  meta: PagingMeta
}

export interface CreateCollectionArgs {
  name: string
  isPrivate: boolean
}

export interface UpdateCollectionArgs {
  ref: CollectionRef
  name: string
  isPrivate: boolean
}

export interface CreateFromLinkArgs {
  url: string
  collectionRef?: CollectionRef
  isPublic?: boolean
}

export interface CreateFromFileArgs {
  file: File
  collectionRef?: CollectionRef
  isPublic?: boolean
}

export interface RefreshFromLinkArgs {
  collectionRef: CollectionRef
}

export interface RefreshFromLinkResponse {
  success: boolean
  message: string
}

export interface ReorderPlaceInCollectionArgs {
  collectionRef: CollectionRef
  placeRef: PlaceRef
  targetPlaceRef: PlaceRef
  position: "before" | "after"
}

export interface ReorderPlaceInCollectionResponse {
  favorites: FavoritePlace[]
}

export const collectionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCollection: builder.query<CollectionResponse, GetCollectionArgs>({
      query: (args) => {
        const {
          ref,
          page = 1,
          perPage = COLLECTION_PLACES_PER_PAGE,
          viewToken,
        } = args
        let url = `/api/saved_collections/${ref}?page=${page}&per_page=${perPage}`
        if (viewToken) {
          url += `&t=${viewToken}`
        }
        return { url }
      },
      providesTags: (_result, _error, { ref }) => [
        { type: "Collection", id: ref },
      ],
      serializeQueryArgs: ({ queryArgs }) => {
        // Cache key includes ref and viewToken, but NOT page
        // This allows all pages to share the same cache entry
        return `${queryArgs.ref}-${queryArgs.viewToken || "default"}`
      },
      forceRefetch({ currentArg, previousArg }) {
        // Only refetch when a component changes its page arg (pagination)
        // Don't refetch when a new component subscribes with a different page
        if (!previousArg) return false
        return currentArg?.page !== previousArg?.page
      },
    }),
    getCollections: builder.query<GetCollectionsResponse, GetCollectionsArgs>({
      query: (args) => {
        const {
          isPublic,
          page = 1,
          perPage = DEFAULT_PLACES_PER_PAGE,
          placeRef,
          q,
          includeFavorites,
        } = args
        let url = `/api/saved_collections?page=${page}&per_page=${perPage}`
        if (isPublic !== undefined) {
          url += `&public=${isPublic}`
        }
        if (placeRef) {
          url += `&place_ref=${placeRef}`
        }
        if (q) {
          url += `&q=${encodeURIComponent(q)}`
        }
        if (includeFavorites) {
          url += "&include_favorites=true"
        }
        return { url }
      },
      providesTags: ["Collections"],
      serializeQueryArgs: ({ queryArgs }) => {
        // Cache key excludes page for pagination merge
        return JSON.stringify({
          isPublic: queryArgs.isPublic,
          placeRef: queryArgs.placeRef,
          q: queryArgs.q,
          includeFavorites: queryArgs.includeFavorites,
        })
      },
      merge: (current, incoming, { arg }) => {
        // Merge pagination results - append pages after page 1
        if (arg.page && arg.page > 1 && current) {
          // Only append when loading a page beyond what we already merged.
          // Tag invalidation re-fetches originalArgs (often the last page);
          // appending that again would duplicate rows.
          if (arg.page > current.meta.current_page) {
            return {
              ...incoming,
              data: [...current.data, ...incoming.data],
            }
          }
          return current
        }
        // For page 1 (or no page specified): replace cache with fresh data
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        // Pages share one cache entry. Remounting with no page after load-more
        // must not look like a page change (undefined !== N) and wipe the merge.
        if (currentArg?.page == null) {
          return false
        }
        return currentArg.page !== previousArg?.page
      },
    }),
    getCollectionsStats: builder.query<SavedCollectionsStats, void>({
      query: (_arg) => ({
        url: "/api/saved_collections/stats",
      }),
      providesTags: ["CollectionsStats"],
    }),
    getCollectionsCount: builder.query<number, boolean | undefined>({
      query: (publicOnly) => {
        let url = "/api/saved_collections/count"
        if (publicOnly !== undefined) {
          url += `?public=${publicOnly}`
        }
        return { url }
      },
      transformResponse: (response: { count: number }) => response.count,
      providesTags: ["CollectionsStats"],
    }),
    getPublicUserCollections: builder.query<
      GetPublicUserCollectionsResponse,
      GetPublicUserCollectionsArgs
    >({
      query: (args) => {
        const { handle, page = 1, perPage = DEFAULT_PLACES_PER_PAGE } = args
        const url = `/api/saved_collections?handle=${handle}&public=true&page=${page}&per_page=${perPage}`
        return { url }
      },
      providesTags: (_result, _error, { handle }) => [
        { type: "PublicUserCollections", id: handle },
      ],
      serializeQueryArgs: ({ queryArgs }) => {
        // Cache key excludes page for pagination merge
        return queryArgs.handle
      },
      merge: (current, incoming, { arg }) => {
        // Merge pagination results - append pages after page 1
        if (arg.page && arg.page > 1 && current) {
          // Only append when loading a page beyond what we already merged.
          // Tag invalidation re-fetches originalArgs (often the last page);
          // appending that again would duplicate rows.
          if (arg.page > current.meta.current_page) {
            return {
              ...incoming,
              data: [...current.data, ...incoming.data],
            }
          }
          return current
        }
        // For page 1 (or no page specified): replace cache with fresh data
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        // Pages share one cache entry. Remounting with no page after load-more
        // must not look like a page change (undefined !== N) and wipe the merge.
        if (currentArg?.page == null) {
          return false
        }
        return currentArg.page !== previousArg?.page
      },
    }),
    getPublicUserCollectionsCount: builder.query<number, string>({
      query: (handle) => ({
        url: `/api/saved_collections/count?handle=${handle}`,
      }),
      transformResponse: (response: { count: number }) => response.count,
      providesTags: (_result, _error, handle) => [
        { type: "PublicUserCollections", id: handle },
      ],
    }),
    createCollection: builder.mutation<CollectionSummary, CreateCollectionArgs>(
      {
        query: (args) => ({
          url: "/api/saved_collections",
          method: "POST",
          data: {
            saved_collection: {
              name: args.name,
              public: !args.isPrivate,
            },
          },
        }),
        transformResponse: (response: {
          saved_collection: CollectionSummary
        }) => response.saved_collection,
        invalidatesTags: ["Collections", "CollectionsStats"],
      }
    ),
    updateCollection: builder.mutation<CollectionSummary, UpdateCollectionArgs>(
      {
        query: (args) => ({
          url: `/api/saved_collections/${args.ref}`,
          method: "PUT",
          data: {
            saved_collection: {
              name: args.name,
              public: !args.isPrivate,
            },
          },
        }),
        transformResponse: (response: {
          saved_collection: CollectionSummary
        }) => response.saved_collection,
        async onQueryStarted(
          { ref, name, isPrivate },
          { dispatch, getState, queryFulfilled }
        ) {
          const patches: Array<{ undo: () => void }> = []

          patches.push(
            dispatch(
              collectionsApi.util.updateQueryData(
                "getCollection",
                { ref, page: 1 },
                (draft) => {
                  if (draft?.data) {
                    draft.data.name = name
                    draft.data.public = !isPrivate
                  }
                }
              )
            )
          )

          // Optimistically update name/privacy in all cached getCollections lists.
          // Don't rely on tag invalidation for this: after infinite-scroll load-more,
          // invalidation re-fetches the last page and merge must not re-append it.
          const cachedArgs = collectionsApi.util.selectCachedArgsForQuery(
            getState(),
            "getCollections"
          )
          cachedArgs.forEach((args) => {
            patches.push(
              dispatch(
                collectionsApi.util.updateQueryData(
                  "getCollections",
                  args,
                  (draft) => {
                    const collection = draft.data.find((c) => c.ref === ref)
                    if (collection) {
                      collection.name = name
                      collection.public = !isPrivate
                    }
                  }
                )
              )
            )
          })

          try {
            await queryFulfilled
          } catch {
            // Revert optimistic updates on error
            patches.forEach((patch) => patch.undo())
          }
        },
        // Don't invalidate Collection/Collections tags - optimistic updates already
        // have correct data. Only invalidate stats so privacy counts stay correct.
        invalidatesTags: ["CollectionsStats"],
      }
    ),
    deleteCollection: builder.mutation<void, CollectionRef>({
      query: (ref) => ({
        url: `/api/saved_collections/${ref}`,
        method: "DELETE",
      }),
      transformResponse: () => undefined,
      async onQueryStarted(ref, { dispatch, getState, queryFulfilled }) {
        // Optimistically remove the collection from all cached getCollections queries
        const patches: Array<{ undo: () => void }> = []

        const cachedArgs = collectionsApi.util.selectCachedArgsForQuery(
          getState(),
          "getCollections"
        )
        cachedArgs.forEach((args) => {
          patches.push(
            dispatch(
              collectionsApi.util.updateQueryData(
                "getCollections",
                args,
                (draft) => {
                  draft.data = draft.data.filter((c) => c.ref !== ref)
                }
              )
            )
          )
        })

        try {
          await queryFulfilled
        } catch {
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (_result, _error, ref) => [
        { type: "Collection", id: ref },
        "Collections",
        "CollectionsStats",
      ],
    }),
    createFromLink: builder.mutation<void, CreateFromLinkArgs>({
      query: (args) => {
        const params: {
          url: string
          public?: boolean
          collection_ref?: CollectionRef
        } = {
          url: args.url,
        }
        if (args.collectionRef) {
          params.collection_ref = args.collectionRef
        }
        if (args.isPublic !== undefined) {
          params.public = args.isPublic
        }
        return {
          url: "/api/saved_collections/create_from_url",
          method: "POST",
          data: params,
        }
      },
      transformResponse: () => undefined,
    }),
    createFromFile: builder.mutation<void, CreateFromFileArgs>({
      query: (args) => {
        const formData = new FormData()
        formData.append("file", args.file)
        if (args.collectionRef) {
          formData.append("collection_ref", args.collectionRef)
        }
        if (args.isPublic !== undefined) {
          formData.append("public", args.isPublic.toString())
        }
        return {
          url: "/api/saved_collections/create_from_file",
          method: "POST",
          data: formData,
          headers: {
            "content-type": "multipart/form-data",
          },
        }
      },
      transformResponse: () => undefined,
    }),
    reorderPlaceInCollection: builder.mutation<
      ReorderPlaceInCollectionResponse,
      ReorderPlaceInCollectionArgs
    >({
      query: (args) => ({
        url: `/api/saved_collections/${args.collectionRef}/move_place`,
        method: "POST",
        data: {
          place_ref: args.placeRef,
          target_place_ref: args.targetPlaceRef,
          position: args.position,
        },
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          collectionsApi.util.updateQueryData(
            "getCollection",
            { ref: args.collectionRef },
            (draft) => {
              const favorites = draft.data.favorites
              const fromIndex = favorites.findIndex(
                (f) => f.entity.entity_ref === args.placeRef
              )
              const targetIndex = favorites.findIndex(
                (f) => f.entity.entity_ref === args.targetPlaceRef
              )
              if (
                fromIndex === -1 ||
                targetIndex === -1 ||
                fromIndex === targetIndex
              )
                return

              const [moved] = favorites.splice(fromIndex, 1)
              // splice shifts indices after removal — recompute relative to the new array
              const adjustedTarget = favorites.findIndex(
                (f) => f.entity.entity_ref === args.targetPlaceRef
              )
              const insertAt =
                args.position === "before" ? adjustedTarget : adjustedTarget + 1
              favorites.splice(insertAt, 0, moved)
            }
          )
        )

        try {
          await queryFulfilled
          dispatch(
            collectionsApi.util.invalidateTags([
              { type: "Collection", id: args.collectionRef },
            ])
          )
        } catch (error) {
          patch.undo()
          toastManager.add({
            type: "error",
            description: "Couldn't reorder — please try again",
          })
          Honeybadger.addBreadcrumb(
            "reorderPlaceInCollection failed, reverted cache",
            {
              category: "error",
              metadata: {
                collectionRef: args.collectionRef,
                placeRef: args.placeRef,
                targetPlaceRef: args.targetPlaceRef,
                position: args.position,
                error: errorMessage(error),
              },
            }
          )
        }
      },
    }),
    refreshFromLink: builder.mutation<
      RefreshFromLinkResponse,
      RefreshFromLinkArgs
    >({
      query: (args) => ({
        url: "/api/saved_collections/refresh_from_link",
        method: "POST",
        data: { collectionRef: args.collectionRef },
      }),
      invalidatesTags: (_result, _error, { collectionRef }) => [
        { type: "Collection", id: collectionRef },
        "Collections",
      ],
    }),
    getFavoriteCollections: builder.query<FavoriteCollection[], void>({
      query: (_arg) => ({ url: "/api/favorite_collections" }),
      transformResponse: (response: {
        favorite_collections: FavoriteCollection[]
      }) => response.favorite_collections,
      providesTags: ["FavoriteCollections"],
      keepUnusedDataFor: Infinity,
    }),
    addFavoriteCollection: builder.mutation<
      FavoriteCollection,
      Pick<Collection, "ref">
    >({
      query: (collection) => ({
        url: "/api/favorite_collections",
        method: "POST",
        data: { collection_ref: collection.ref },
      }),
      transformResponse: (response: {
        favorite_collection: FavoriteCollection
      }) => response.favorite_collection,
      invalidatesTags: [
        "FavoriteCollections",
        "Collections",
        "CollectionsStats",
      ],
      async onQueryStarted(collection, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          collectionsApi.util.updateQueryData(
            "getFavoriteCollections",
            undefined,
            (draft) => {
              draft.unshift({
                collection_ref: collection.ref,
                updated_at: now(),
              })
            }
          )
        )
        const countPatch = dispatch(
          adjustFavoriteCollectionsCount(collection.ref, 1)
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
          countPatch.undo()
        }
      },
    }),
    removeFavoriteCollection: builder.mutation<void, Pick<Collection, "ref">>({
      query: (collection) => ({
        url: `/api/favorite_collections/${collection.ref}`,
        method: "DELETE",
      }),
      transformResponse: () => undefined,
      invalidatesTags: [
        "FavoriteCollections",
        "Collections",
        "CollectionsStats",
      ],
      async onQueryStarted(collection, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          collectionsApi.util.updateQueryData(
            "getFavoriteCollections",
            undefined,
            (draft) => {
              const index = draft.findIndex(
                (f) => f.collection_ref === collection.ref
              )
              if (index !== -1) draft.splice(index, 1)
            }
          )
        )
        const countPatch = dispatch(
          adjustFavoriteCollectionsCount(collection.ref, -1)
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
          countPatch.undo()
        }
      },
    }),
  }),
})

// The collection page's save count, kept in step with the viewer's save or unsave
function adjustFavoriteCollectionsCount(ref: CollectionRef, delta: number) {
  return collectionsApi.util.updateQueryData(
    "getCollection",
    { ref },
    (draft) => {
      draft.data.favorite_collections_count += delta
    }
  )
}

export const {
  useGetCollectionQuery,
  useLazyGetCollectionQuery,
  useGetCollectionsQuery,
  useLazyGetCollectionsQuery,
  useGetCollectionsStatsQuery,
  useGetCollectionsCountQuery,
  useGetPublicUserCollectionsQuery,
  useLazyGetPublicUserCollectionsQuery,
  useGetPublicUserCollectionsCountQuery,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useDeleteCollectionMutation,
  useCreateFromLinkMutation,
  useCreateFromFileMutation,
  useRefreshFromLinkMutation,
  useReorderPlaceInCollectionMutation,
  useAddFavoriteCollectionMutation,
  useRemoveFavoriteCollectionMutation,
} = collectionsApi

const selectFavoriteCollectionRefs = createSelector(
  collectionsApi.endpoints.getFavoriteCollections.select(),
  (result) =>
    result.data && result.data.length > 0
      ? new Set(result.data.map((f) => f.collection_ref))
      : getConstantEmptySet<CollectionRef>()
)

export const selectIsFavoriteCollection = (
  state: RootState,
  collectionRef: CollectionRef
) => selectFavoriteCollectionRefs(state).has(collectionRef)
